import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { OB_PAGE_ACCESS_CONTEXT } from 'constants/cookieConstants';

import BrowserStorage from 'utils/globals/browserStorage';

import { ENTITY_TYPE_ID_PREFIX } from 'operabase-router/constants';
import getEntityIdWithPrefix from 'operabase-router/utils/getEntityIdWithPrefix';

import { useEntityTypeGroupedPageAccesses } from './useAuthenticatedUser';

const PageAccessContext = createContext();

const PageAccessContextProvider = ({ children }) => {
  const [activePageAccessState, setActivePageAccessState] = useState({ activePageAccessId: null, isReady: false });
  const { data: groupedPageAccesses, isLoading } = useEntityTypeGroupedPageAccesses();

  const { activePageAccessId, isReady } = activePageAccessState || {};

  const getMatchingPageAccess = useCallback(
    id => {
      if (!id || isLoading) {
        return null;
      }

      const entityType = Object.keys(ENTITY_TYPE_ID_PREFIX).find(key => id.startsWith(ENTITY_TYPE_ID_PREFIX[key]));

      if (entityType) {
        const entityId = Number(id.replace(ENTITY_TYPE_ID_PREFIX[entityType], ''));
        const matchedEntity = groupedPageAccesses?.[entityType]?.find(item => item?.entity?.id === entityId);

        if (matchedEntity?.entity?.id) {
          return {
            entity: matchedEntity?.entity,
            entityType,
          };
        }
      }

      return null;
    },
    [groupedPageAccesses, isLoading],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && !isLoading) {
      const storedPageAccessId = BrowserStorage.cookies.get({ key: OB_PAGE_ACCESS_CONTEXT });

      if (storedPageAccessId) {
        const matchedPageAccess = getMatchingPageAccess(storedPageAccessId);

        if (matchedPageAccess) {
          setActivePageAccessState({ activePageAccessId: storedPageAccessId, isReady: true });
        } else {
          setActivePageAccessState({ activePageAccessId: null, isReady: true });
        }
      } else {
        setActivePageAccessState({ activePageAccessId: null, isReady: true });
      }
    }
  }, [isLoading, groupedPageAccesses, getMatchingPageAccess, setActivePageAccessState]);

  const activePageAccessContext = useMemo(() => {
    if (!activePageAccessId) {
      return null;
    }

    const matchedPageAccess = getMatchingPageAccess(activePageAccessId);

    return matchedPageAccess;
  }, [activePageAccessId, getMatchingPageAccess]);

  const setPageAccessContext = useCallback(
    ({ entityType, entity }) => {
      const { id: selectedPageContextId } = getEntityIdWithPrefix({ entity, entityType });

      if (selectedPageContextId) {
        BrowserStorage.cookies.set({
          key: OB_PAGE_ACCESS_CONTEXT,
          value: selectedPageContextId,
        });

        setActivePageAccessState({ activePageAccessId: selectedPageContextId, isReady: true });
      }
    },
    [setActivePageAccessState],
  );

  const value = useMemo(
    () => ({
      isReady,
      activePageAccessContext,
      activePageAccessId,
      setPageAccessContext,
    }),
    [activePageAccessContext, activePageAccessId, setPageAccessContext, isReady],
  );

  return <PageAccessContext.Provider value={value}>{children}</PageAccessContext.Provider>;
};

const usePageAccessContext = () => useContext(PageAccessContext);

export { PageAccessContextProvider };
export default usePageAccessContext;
